import { el } from "./dom";

// Setup items by module (brief 1E-7, mockup v59): every setup list shows the module you came from
// plus Shared, with a picker (that module + Shared, each module the company has, Shared,
// everything this company has). The shell remembers the last module you were in; the server
// decides what each picker value shows and answers the picker's options.

const FROM_KEY = "fc-from-module";
const ITEM_MODULES = ["staffing", "homecare", "transport", "jobs"];

/** The shell calls this on every screen: a module's own screens set where you came from. */
export function noteModule(section: string): void {
  if (!ITEM_MODULES.includes(section)) return;
  try {
    sessionStorage.setItem(FROM_KEY, section);
  } catch {
    /* private window: setup lists start on everything the company has */
  }
}

/** The module you came from (null: Company settings). */
export function cameFrom(): string | null {
  try {
    return sessionStorage.getItem(FROM_KEY);
  } catch {
    return null;
  }
}

export interface Picker {
  value: string;
  options: { value: string; label: string }[];
}

/** One list's picker value, kept while the page is open. */
const picked = new Map<string, string>();

/**
 * The query a setup list asks with: where you came from (a module's own screen, such as Intake
 * questions in Home Care, is always from its module) and the picker's value for this list.
 */
export function setupQuery(list: string, own?: string): string {
  const q = new URLSearchParams();
  const from = own ?? cameFrom();
  if (from) q.set("from", from);
  const show = picked.get(list);
  if (show) q.set("show", show);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * The mockup's note: "<noun> belong to a module or to Shared. You see the module you came from and
 * Shared; items for modules the company doesn't have never show." with the picker.
 */
export function setupBar(
  list: string,
  noun: string,
  picker: Picker | undefined,
  onChange: () => void,
): HTMLElement {
  const select = el(
    "select",
    { "aria-label": `Show ${noun}` },
    ...(picker?.options ?? []).map((o) =>
      el("option", { value: o.value, selected: o.value === picker?.value }, o.label),
    ),
  );
  select.addEventListener("change", () => {
    picked.set(list, select.value);
    onChange();
  });
  return el(
    "div",
    {
      class: "note setupbar",
      style: "display:flex;gap:10px;align-items:center;flex-wrap:wrap",
      "data-setup-bar": list,
    },
    el(
      "p",
      { style: "flex:1;min-width:220px;margin:0" },
      `${noun} belong to a module or to Shared.`,
    ),
    select,
  );
}
